/**
 * Cloud File Service
 * 
 * Handles file upload, download, listing, and folder operations for cloud providers
 */

import api from '../api'
import { SYNC_CONFIG } from '../syncConfig'

class CloudFileService {
  /**
   * Upload file content to cloud storage
   */
  async uploadFileContent(content, fileName, folderId = null, isConnected, accessToken, connectedProvider) {
    // CRITICAL: Check if user is authenticated before uploading file
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (!authToken || !user) {
      console.log('🔒 CloudFileService: User not authenticated, blocking file upload')
      throw new Error('User not authenticated. Please log in first.')
    }
    
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 CloudFileService: Onboarding detected, skipping file upload')
      throw new Error('File upload not available during onboarding')
    }
    
    try {
      if (!isConnected || !accessToken) {
        throw new Error('Not connected to cloud storage')
      }

      console.log(`🌐 CloudFileService: Uploading file to ${connectedProvider}:`, fileName)

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const endpoint = connectedProvider === 'google_drive' ? `${API_BASE_URL}/api/cloud/google-drive/files` : null
      if (!endpoint) {
        throw new Error(`Unsupported provider: ${connectedProvider}`)
      }

      // Use axios API to benefit from automatic token refresh interceptor
      const response = await api.post('/api/cloud/google-drive/files', {
        content,
        file_name: fileName,
        folder_id: folderId,
      })

      // Axios automatically handles status codes and throws for 4xx/5xx
      // The interceptor will handle 401s automatically
      const data = response.data
      console.log('✅ CloudFileService: File uploaded successfully:', data.id)
      
      return { success: true, data }

    } catch (error) {
      console.error('❌ CloudFileService: File upload error:', error)
      throw error
    }
  }

  /**
   * Download file content from cloud storage
   */
  async downloadFileContent(fileId, isConnected, accessToken, connectedProvider) {
    // CRITICAL: Check if user is authenticated before downloading file
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (!authToken || !user) {
      console.log('🔒 CloudFileService: User not authenticated, blocking file download')
      throw new Error('User not authenticated. Please log in first.')
    }
    
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 CloudFileService: Onboarding detected, skipping file download')
      throw new Error('File download not available during onboarding')
    }
    
    try {
      if (!isConnected || !accessToken) {
        throw new Error('Not connected to cloud storage')
      }

      console.log(`🌐 CloudFileService: Downloading file from ${connectedProvider}:`, fileId)

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const endpoint = connectedProvider === 'google_drive' ? 
        `${API_BASE_URL}/api/cloud/google-drive/files/${fileId}/content` : null
      if (!endpoint) {
        throw new Error(`Unsupported provider: ${connectedProvider}`)
      }

      // Use axios API to benefit from automatic token refresh interceptor
      const response = await api.get(`/api/cloud/google-drive/files/${fileId}/content`)

      // Axios automatically handles status codes and throws for 4xx/5xx
      // The interceptor will handle 401s automatically
      const data = response.data
      console.log('✅ CloudFileService: File downloaded successfully')
      
      return { success: true, content: data.content }

    } catch (error) {
      console.error('❌ CloudFileService: File download error:', error)
      throw error
    }
  }

  /**
   * List files in cloud storage folder
   * Note: For frontend-only sync, this should not be used as it calls the backend
   */
  async listFiles(folderId = null, isConnected, accessToken, connectedProvider) {
    try {
      // Check if we're using frontend-only sync
      if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
        console.warn('⚠️ CloudFileService: listFiles() called with frontend-only sync enabled')
        console.warn('⚠️ CloudFileService: Frontend sync should use googleDriveAPI directly, not backend endpoints')
        
        // Return empty result to avoid backend calls
        return { 
          success: true, 
          files: [],
          message: 'Frontend sync uses googleDriveAPI directly - backend listFiles not needed'
        }
      }

      if (!isConnected || !accessToken) {
        throw new Error('Not connected to cloud storage')
      }

      console.log(`🌐 CloudFileService: Listing files in ${connectedProvider}`)

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const endpoint = connectedProvider === 'google_drive' ? `${API_BASE_URL}/api/cloud/google-drive/files` : null
      if (!endpoint) {
        throw new Error(`Unsupported provider: ${connectedProvider}`)
      }

      // Get user ID for the backend request
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const userId = user.id || user.email
      
      if (!userId) {
        throw new Error('User ID not found for cloud storage request')
      }
      
      let url = `/api/cloud/google-drive/files?userId=${encodeURIComponent(userId)}`
      if (folderId) {
        url += `&folderId=${encodeURIComponent(folderId)}`
      }

      // Use axios API to benefit from automatic token refresh interceptor
      const response = await api.get(url)

      // Axios automatically handles status codes and throws for 4xx/5xx
      // The interceptor will handle 401s automatically
      const data = response.data
      console.log('✅ CloudFileService: Files listed successfully:', data.length)
      
      return { success: true, files: data }

    } catch (error) {
      console.error('❌ CloudFileService: File listing error:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Create folder in cloud storage
   */
  async createFolder(folderName, parentFolderId = null, isConnected, accessToken, connectedProvider) {
    // CRITICAL: Check if user is authenticated before creating folder
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (!authToken || !user) {
      console.log('🔒 CloudFileService: User not authenticated, blocking folder creation')
      throw new Error('User not authenticated. Please log in first.')
    }
    
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 CloudFileService: Onboarding detected, skipping folder creation')
      throw new Error('Folder creation not available during onboarding')
    }
    
    try {
      if (!isConnected || !accessToken) {
        throw new Error('Not connected to cloud storage')
      }

      console.log(`🌐 CloudFileService: Creating folder in ${connectedProvider}:`, folderName)

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const endpoint = connectedProvider === 'google_drive' ? `${API_BASE_URL}/api/cloud/google-drive/folders` : null
      if (!endpoint) {
        throw new Error(`Unsupported provider: ${connectedProvider}`)
      }

      // Use axios API to benefit from automatic token refresh interceptor
      const response = await api.post('/api/cloud/google-drive/folders', {
        name: folderName,
        parent_id: parentFolderId,
      })

      // Axios automatically handles status codes and throws for 4xx/5xx
      // The interceptor will handle 401s automatically
      const data = response.data
      console.log('✅ CloudFileService: Folder created successfully:', data.id)
      
      return { success: true, data }

    } catch (error) {
      console.error('❌ CloudFileService: Folder creation error:', error)
      throw error
    }
  }

  /**
   * Get storage quota from cloud provider
   */
  async getStorageQuota(isConnected, accessToken) {
    if (!isConnected || !accessToken) {
      return {
        success: false,
        error: 'Google Drive not connected'
      }
    }
    
    try {
      console.log('🔍 CloudFileService: Getting storage quota from backend...')
      
      const response = await api.get('/api/cloud/google-drive/quota')
      
      if (response.status === 200) {
        const quotaData = response.data
        console.log('✅ CloudFileService: Storage quota retrieved successfully via backend')
        
        // Return the expected structure that CloudStorageUsage component expects
        return {
          success: true,
          quota: quotaData.storageQuota || quotaData
        }
      } else {
        return {
          success: false,
          error: `Failed to get storage quota: ${response.status}`
        }
      }
      
    } catch (error) {
      console.error('❌ CloudFileService: Error getting storage quota:', error)
      return {
        success: false,
        error: error.message || 'Failed to get storage quota'
      }
    }
  }
}

export default new CloudFileService()
